import React from 'react';
import cx from 'classnames';
import { MenuRightIcon } from '@mc/wink-icons';
import { ChevronRight } from '@design-systems/icons';
import useId from '@mc/hooks/useId';
import useWinkBreakpoints from '@mc/hooks/useWinkBreakpoints';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import { useExperiment } from '@mc/experiments';
import { useReferrer } from '@mc/router';
import { PageSwitcher, PageSwitcherItem } from '../PageHeader';
import Text from '../Text';
import Badge from '../Badge';
import Button from '../Button';
import { useWizardActions, useWizardState, useWizardInventory } from './Wizard';
import { TranslateWizardBreadcrumbs } from './TranslateWizardBreadcrumbs';
import stylesheet from './WizardBreadcrumbs.css';

export type WizardBreadcrumbsProps = {
  interactive?: boolean;
  onNavigate?: (currentStep: string | null, stepName: string) => boolean;
  /** Unsafe! Only use when your Wizard has branching flows. This should not be the norm. */
  unsafe_hideFutureSteps?: boolean;
  /** Style variant for different breadcrumb appearances */
  variant?: 'default' | 'revised';
};

/**
 * In the forest, the wizard finds breadcrumbs to follow.
 *
 * In this implementation, breadcrumbs can only navigate backward; all future
 * steps appear as disabled buttons. Future steps may depend on previous ones,
 * so we cannot assume that, if someone backtracks two steps, they can navigate
 * forward two steps again in a single bound.
 */
const WizardBreadcrumbs = ({
  onNavigate,
  interactive = true,
  unsafe_hideFutureSteps = false,
  variant = 'default',
}: WizardBreadcrumbsProps) => {
  const { currentStep, stepNames, displayNames } = useWizardState();
  const { isPhone, isTablet } = useWinkBreakpoints();
  const { navigate } = useWizardActions();
  const inventory = useWizardInventory();
  const wizardActions = useWizardActions();
  const id = useId();
  const currentStepIndex = stepNames?.indexOf(currentStep ?? '') || 0;
  const referrer = useReferrer();

  const isAddContactsExperiment =
    useExperiment('growth-import-contacts-has-contacts') === 'variant' &&
    mcdsFlagCheck(FLAGS.GROWTH_IMPORT_CONTACTS_HAS_CONTACTS) &&
    referrer.includes('/campaigns/edit?id=');

  const {
    wizardBreadcrumbsLabelText,
    wizardBreadcrumbsStepText,
    wizardBreadcrumbsFutureStepText,
  } = TranslateWizardBreadcrumbs(currentStepIndex + 1, stepNames?.length ?? 0);
  if (!stepNames) {
    // Nothing to render if we don't know the step names.
    return null;
  }
  const isFocusedStatePageHeaderOn = mcdsFlagCheck(
    'xp_mcds_redesign_focused_state_page_header',
  );

  return (
    <nav aria-labelledby={id}>
      <span id={id} className="wink-visually-hidden">
        {wizardBreadcrumbsLabelText}
      </span>
      {(isFocusedStatePageHeaderOn && isTablet) || isPhone ? (
        <span>
          <strong className={stylesheet.steps}>
            {unsafe_hideFutureSteps
              ? wizardBreadcrumbsStepText
              : wizardBreadcrumbsFutureStepText}
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {interactive ? ' ' : ` ${displayNames[currentStepIndex]}`}
          </strong>
          {interactive && (
            <PageSwitcher label="Pages">
              {stepNames.map((stepName: string, index: number) => {
                const isCurrentStep = currentStep === stepName;
                const isAfterCurrentStep = index > currentStepIndex;
                const displayName = displayNames[index] || stepName;

                if (unsafe_hideFutureSteps && isAfterCurrentStep) {
                  return null;
                }

                return (
                  <PageSwitcherItem
                    key={displayName}
                    label={displayName}
                    aria-current={isCurrentStep ? 'step' : undefined}
                    // @ts-expect-error TS(2322): Type '{ key: any; label: any; "aria-current": "ste... Remove this comment to see the full error message
                    disabled={isAfterCurrentStep}
                    onClick={() => {
                      let shouldNavigate = true;
                      if (onNavigate && currentStep) {
                        shouldNavigate = onNavigate(currentStep, stepName);
                      }
                      if (shouldNavigate !== false) {
                        navigate(stepName);
                      }
                    }}
                  />
                );
              })}
            </PageSwitcher>
          )}
        </span>
      ) : (
        <ol className={stylesheet.breadcrumbs}>
          {stepNames.map((stepName: string, index: number) => {
            const isCurrentStep = currentStep === stepName;
            const isAfterCurrentStep = index > currentStepIndex;
            const displayName = displayNames[index] || stepName;

            if (unsafe_hideFutureSteps && isAfterCurrentStep) {
              return null;
            }

            return (
              <li key={displayName} className={stylesheet.step}>
                {/* icon */}
                {index > 0 &&
                  (isFocusedStatePageHeaderOn && variant === 'revised' ? (
                    <ChevronRight
                      className={cx(
                        stylesheet.revisedSVG,
                        isAfterCurrentStep && stylesheet.disabledIcon,
                      )}
                    />
                  ) : (
                    <MenuRightIcon
                      className={cx(
                        isAfterCurrentStep && stylesheet.disabledIcon,
                      )}
                    />
                  ))}

                {interactive ? (
                  isFocusedStatePageHeaderOn && variant === 'revised' ? (
                    isCurrentStep ? (
                      <Button
                        type="tertiary"
                        size="small"
                        className={cx(
                          stylesheet.revisedStep,
                          stylesheet.current,
                        )}
                        aria-current={undefined}
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                      >
                        {displayName}
                      </Button>
                    ) : (
                      <Button
                        type="quaternary"
                        size="small"
                        className={cx(
                          stylesheet.revisedStep,
                          isAfterCurrentStep && stylesheet.futureStep,
                        )}
                        aria-current={undefined}
                        disabled={isAfterCurrentStep}
                        onClick={() => {
                          if (!isAfterCurrentStep) {
                            let shouldNavigate = true;
                            if (onNavigate) {
                              shouldNavigate = onNavigate(
                                currentStep,
                                stepName,
                              );
                            }
                            if (shouldNavigate !== false) {
                              navigate(stepName);
                            }
                          }
                        }}
                      >
                        {displayName}
                      </Button>
                    )
                  ) : (
                    <button
                      type="button"
                      className={cx(
                        stylesheet.stepName,
                        isCurrentStep && stylesheet.current,
                      )}
                      aria-current={isCurrentStep ? 'step' : undefined}
                      disabled={isCurrentStep || isAfterCurrentStep}
                      onClick={() => {
                        let shouldNavigate = true;
                        if (
                          isAddContactsExperiment &&
                          currentStep === 'review'
                        ) {
                          wizardActions.setInventory((items) => ({
                            ...items,
                            tags: inventory.tagsCopyForAddContactsExperiment,
                          }));
                        }
                        if (onNavigate) {
                          shouldNavigate = onNavigate(currentStep, stepName);
                        }
                        if (shouldNavigate !== false) {
                          navigate(stepName);
                        }
                      }}
                    >
                      {displayName}
                    </button>
                  )
                ) : isFocusedStatePageHeaderOn && variant === 'revised' ? (
                  isCurrentStep ? (
                    <Badge
                      className={cx(stylesheet.revisedStep, stylesheet.current)}
                      aria-current="step"
                    >
                      {displayName}
                    </Badge>
                  ) : (
                    <Text
                      appearance="small"
                      className={cx(
                        stylesheet.revisedStep,
                        isAfterCurrentStep
                          ? stylesheet.futureNotInteractiveStep
                          : stylesheet.pastNotInteractiveStep,
                      )}
                    >
                      {displayName}
                    </Text>
                  )
                ) : (
                  <Text
                    appearance="small"
                    className={cx(
                      stylesheet.stepName,
                      isCurrentStep && stylesheet.current,
                      !isCurrentStep && stylesheet.notInteractive,
                    )}
                    aria-current={isCurrentStep ? 'step' : undefined}
                  >
                    {displayName}
                  </Text>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
};

export default WizardBreadcrumbs;
